<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, onBeforeMount, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useFetchWrapper } from "@core/composables"
import { getUserFacingDescription } from "@core/exceptions"
import { formatDate, formatNumber } from "@core/helpers"
import { track } from "@core/tracking/track"
import type { ColumnConfig } from "@types"
import type { Invoice } from "@modules/espace-entreprise/types/invoice"
import type { Services } from "@services/service"
import moment from "moment-timezone"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Factures",
            ttc: "Toutes taxes comprises",
            download_all_invoices: "Télécharger toutes les factures",
            empty_list: "Aucune facture disponible",
            invoices_load_error:
                "Nous rencontrons un problème pour afficher vos factures pour le moment. Veuillez réessayer plus tard.",
            download_all_error:
                "Le téléchargement de vos factures a échoué. Veuillez réessayer dans quelques minutes ; si le problème persiste, contactez le support.",
            columns: {
                emission_date: "Émise le",
                amount: "Montant",
                amount_ttc: "Montant TTC *",
                status: "Statut",
                paid_date: "Réglée le",
                actions: "Actions",
            },
            status: {
                draft: "Pas envoyée",
                open: "Non payée",
                paid: "Payée",
                uncollectible: "Non collectée",
                void: "Annulée",
            },
            menu: {
                download: "Télécharger le PDF",
                see: "Voir en ligne",
            },
        },
    },
})

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")

// ================================
// COMPOSABLES
// ================================
const route = useRoute()

/** Surface de tracking : nom logique de la page hôte (PageCompany / PageSubscriptionCancelled / …). */
const trackingSurface = computed(() =>
    typeof route?.name === "string" ? route.name : undefined
)

// ================================
// STATE
// ================================
const invoices = ref<Invoice[]>([])
const downloadAllInvoicesLoading = ref(false)
/**
 * Message d'échec du téléchargement groupé (DEV-1088).
 *
 * L'erreur n'était que `console.error` : un 500 serveur était strictement
 * indiscernable d'un bouton mort — le symptôme exact remonté par le support.
 */
const downloadAllError = ref<string | null>(null)

// ================================
// FETCH WRAPPERS
// ================================
const {
    fetch: fetchInvoices,
    isFetching: isFetchingInvoices,
    hasError: hasErrorInvoices,
    error: errorInvoices,
} = useFetchWrapper(
    async (controller?: AbortController) => {
        if (!services?.$getInvoices) {
            throw new Error("Service $getInvoices non disponible")
        }

        const result = await services.$getInvoices(controller)

        // Gérer les deux formats de réponse possibles
        if (Array.isArray(result)) {
            invoices.value = result
        } else if (result?.data && Array.isArray(result.data)) {
            invoices.value = result.data
        } else {
            invoices.value = []
        }
    },
    (error) => {
        console.error("Erreur lors de la récupération des factures:", error)
    }
)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Message affiché quand le chargement des factures échoue.
 *
 * Même règle que pour le téléchargement groupé : l'explication rédigée par
 * l'API (`ServiceError.description`) prime sur le libellé maison.
 */
const invoicesLoadErrorMessage = computed(
    () =>
        getUserFacingDescription(errorInvoices.value) ??
        t("invoices_load_error")
)

/**
 * Formate la date d'émission d'une facture
 */
const formatEmissionDate = (invoice: Invoice) => {
    return formatDate(
        new Date(invoice.finalized_at * 1000),
        "DD/MM/YYYY",
        "-",
        null
    )
}

/**
 * Formate le montant d'une facture
 */
const formatAmount = (invoice: Invoice) => {
    return formatNumber(invoice.total / 100, {
        style: "currency",
        currency: "EUR",
    })
}

/**
 * Formate la date de paiement d'une facture
 */
const formatPaidDate = (invoice: Invoice) => {
    if (!invoice.paid_at) return "-"
    return formatDate(new Date(invoice.paid_at * 1000), "DD/MM/YYYY", "-", null)
}

/**
 * Récupère le label du statut
 */
const getStatusLabel = (invoice: Invoice) => {
    return t(`status.${invoice.status}`) || invoice.status || "Inconnu"
}

/**
 * Indique si le statut est "open" (danger)
 */
const isStatusOpen = (invoice: Invoice) => {
    return invoice.status === "open"
}

/**
 * Configuration des colonnes du tableau des factures
 */
const invoiceColumns = computed<ColumnConfig[]>(() => [
    {
        field: "finalized_at",
        header: `${t("columns.emission_date")} (${invoices.value.length})`,
        sortable: false,
        style: { width: "11.5rem" },
    },
    {
        field: "total",
        header: t("columns.amount_ttc"),
        sortable: false,
        style: { width: "14rem" },
    },
    {
        field: "status",
        header: t("columns.status"),
        sortable: false,
        style: { width: "14rem" },
    },
    {
        field: "paid_at",
        header: t("columns.paid_date"),
        sortable: false,
        style: { width: "14rem" },
    },
    {
        field: "actions",
        header: t("columns.actions"),
        sortable: false,
        style: { width: "auto" },
    },
])

/**
 * Télécharge le PDF d'une facture.
 *
 * DEV-1088 — cette action était enfouie dans un menu « ⋯ » sans libellé ni
 * tooltip : le client rapportait n'avoir « aucune option de téléchargement par
 * facture », là où le support, qui savait où cliquer, en sortait 25 en 80 s.
 * Elle est désormais un bouton explicite par ligne.
 */
const onDownloadInvoicePdf = (invoice: Invoice) => {
    if (!invoice.invoice_pdf) return
    track({
        event: "Invoice Downloaded",
        payload: {
            format: "pdf",
            surface: trackingSurface.value,
            invoice_id: invoice._id,
        },
    })
    window.location.assign(invoice.invoice_pdf)
}

/**
 * Ouvre la facture hébergée par Stripe dans un nouvel onglet.
 */
const onOpenInvoiceOnline = (invoice: Invoice) => {
    if (!invoice.hosted_invoice_url) return
    window.open(invoice.hosted_invoice_url, "_blank")
}

// ================================
// METHODS
// ================================

/**
 * Télécharge toutes les factures en archive ZIP
 */
const onDownloadAllInvoices = async () => {
    if (
        downloadAllInvoicesLoading.value ||
        !services?.$downloadInvoicesAsArchive
    ) {
        return
    }

    try {
        downloadAllInvoicesLoading.value = true
        downloadAllError.value = null
        track({
            event: "Invoice Downloaded",
            payload: {
                format: "zip",
                surface: trackingSurface.value,
            },
        })
        const base64Data = await services.$downloadInvoicesAsArchive()

        // Convertir la base64 en ArrayBuffer
        const binaryData = atob(base64Data)
        const arrayBuffer = new ArrayBuffer(binaryData.length)
        const uint8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < binaryData.length; i++) {
            uint8Array[i] = binaryData.charCodeAt(i)
        }

        // Créer un blob à partir de l'ArrayBuffer
        const blob = new Blob([arrayBuffer], { type: "application/zip" })

        // Créer une URL à partir du blob
        const blobURL = URL.createObjectURL(blob)

        // Créer un lien <a> avec l'attribut href configuré sur l'URL du blob
        const downloadLink = document.createElement("a")
        downloadLink.href = blobURL
        downloadLink.download = `Factures du ${moment().format("DD_MM_YYYY")}.zip`

        // Ajouter le lien au DOM
        document.body.appendChild(downloadLink)

        // Déclencher un clic sur le lien pour démarrer le téléchargement
        downloadLink.click()

        // Retirer le lien du DOM après le téléchargement
        document.body.removeChild(downloadLink)

        // Révoquer l'URL du blob après utilisation
        URL.revokeObjectURL(blobURL)
    } catch (error) {
        console.error("Erreur lors du téléchargement des factures:", error)
        // L'API rédige une explication pour le client dans `description`
        // (ex. `error_stripe_download_invoices`) : on la préfère au libellé
        // maison, qui ne sert que de repli. `technicalMessage` — le jargon
        // d'axios — n'est jamais candidat à l'affichage (DEV-1088).
        downloadAllError.value =
            getUserFacingDescription(error) ?? t("download_all_error")
    } finally {
        downloadAllInvoicesLoading.value = false
    }
}

// ================================
// LIFECYCLE
// ================================
onBeforeMount(async () => {
    await fetchInvoices()
})
</script>

<template>
    <BaseTitledCard class="invoice-card">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <!-- ================================ -->
                <!-- ÉTAT D'ERREUR -->
                <!-- ================================ -->
                <BaseMessage v-if="hasErrorInvoices" severity="error">
                    {{ invoicesLoadErrorMessage }}
                </BaseMessage>

                <BaseSkeleton
                    v-else-if="isFetchingInvoices"
                    width="100%"
                    height="30vh"
                    class="mx-auto"
                />

                <!-- ================================ -->
                <!-- TABLEAU DES FACTURES -->
                <!-- ================================ -->
                <template v-else>
                    <BaseTable
                        data-key="_id"
                        :columns="invoiceColumns"
                        :value="invoices"
                        paginator
                        :rows="10"
                        size="small"
                        :loading="isFetchingInvoices"
                        :rows-per-page-options="[5, 10, 25, 50]"
                        :empty-message="t('empty_list')"
                    >
                        <template #cell_finalized_at="{ data }">
                            <p>
                                {{ formatEmissionDate(data) }}
                            </p>
                        </template>
                        <template #cell_total="{ data }">
                            <p>
                                {{ formatAmount(data) }}
                            </p>
                        </template>
                        <template #cell_status="{ data }">
                            <div
                                class="flex items-center gap-2"
                                :class="{
                                    'text-red-600': isStatusOpen(data),
                                }"
                            >
                                <p
                                    :class="{
                                        'text-red-600': isStatusOpen(data),
                                    }"
                                >
                                    {{ getStatusLabel(data) }}
                                </p>
                                <BaseIcon
                                    v-if="isStatusOpen(data)"
                                    icon="pi pi-info-circle"
                                    size="14px"
                                    color="var(--p-red-600)"
                                />
                            </div>
                        </template>
                        <template #cell_paid_at="{ data }">
                            <p>
                                {{ formatPaidDate(data) }}
                            </p>
                        </template>
                        <template #cell_actions="{ data }">
                            <div class="flex justify-end w-full gap-1">
                                <BaseButton
                                    type="button"
                                    icon="pi pi-download"
                                    rounded
                                    text
                                    :disabled="!data.invoice_pdf"
                                    :aria-label="t('menu.download')"
                                    v-tooltip.top="t('menu.download')"
                                    @click="onDownloadInvoicePdf(data)"
                                />
                                <BaseButton
                                    type="button"
                                    icon="pi pi-external-link"
                                    rounded
                                    text
                                    :disabled="!data.hosted_invoice_url"
                                    :aria-label="t('menu.see')"
                                    v-tooltip.top="t('menu.see')"
                                    @click="onOpenInvoiceOnline(data)"
                                />
                            </div>
                        </template>
                    </BaseTable>
                    <BaseMessage v-if="downloadAllError" severity="error">
                        {{ downloadAllError }}
                    </BaseMessage>

                    <div
                        class="flex flex-col md:flex-row gap-4 md:justify-center md:justify-between items-center"
                    >
                        <p class="text-base">
                            <sup>*</sup>
                            {{ t("ttc") }}
                        </p>
                        <BaseButton
                            v-if="invoices.length > 0"
                            :loading="downloadAllInvoicesLoading"
                            :label="t('download_all_invoices')"
                            icon="pi pi-download"
                            severity="secondary"
                            text
                            @click="onDownloadAllInvoices"
                        />
                    </div>
                </template>
            </div>
        </template>
    </BaseTitledCard>
</template>
